<template lang="pug">
base-section#persons
  base-page-content
    .people-shell
      .people-intro
        p.people-kicker {{ t('pages.team.kicker') }}
        h2.people-heading {{ t('pages.team.heading') }}
        p.people-copy {{ t('pages.team.intro') }}
      .people-sections
        section.person-category(
          v-for='section in sections'
          :key='section.name'
        )
          .category-heading
            h2 {{ categoryLabel(section.name) }}
          .people-grid
            person-overview-card(
              v-for='person in section.people'
              :key='person.path'
              :person='person'
            )
</template>

<script setup lang="ts">
import type { PeopleSection } from '~/services/peopleService'

defineOptions({
  name: 'Persons',
})

defineProps({
  sections: {
    type: Array as () => PeopleSection[],
    default: () => [],
  },
})

const { t } = useI18n()

function categoryLabel(name: string) {
  const labels: Record<string, string> = {
    'Leaders of the Research Group': t('pages.team.categories.leaders'),
    'Team Assistants': t('pages.team.categories.assistants'),
    'Team Members': t('pages.team.categories.members'),
  }

  return labels[name] ?? name
}
</script>

<style scoped>
#persons {
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface-muted) 92%, var(--surface-page)) 0%,
      var(--surface-page) 18rem
    ),
    radial-gradient(
      circle at top left,
      rgba(0, 192, 216, 0.08),
      transparent 30rem
    );
}

.people-shell {
  display: grid;
  gap: 3rem;
}

.people-intro {
  padding: 1.5rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.people-kicker {
  margin: 0 0 0.75rem;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.76rem;
  font-weight: 700;
}

.people-heading {
  margin: 0;
  font-size: clamp(1.7rem, 2.8vw, 2.5rem);
  line-height: 0.98;
}

.people-copy {
  max-width: 48rem;
  margin: 1rem 0 0;
  color: var(--color-text);
  line-height: 1.65;
}

.people-note {
  margin: 1rem 0 0;
  color: var(--color-text-muted);
}

.people-sections {
  display: grid;
  gap: 3rem;
}

.person-category {
  scroll-margin-top: 6rem;
}

.category-heading {
  display: flex;
  align-items: baseline;
  margin-bottom: 1.2rem;
  padding-bottom: 0.45rem;
}

.category-heading h2 {
  margin: 0;
  font-size: clamp(1.75rem, 2.5vw, 2.3rem);
  line-height: 0.95;
}

.people-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.4rem;
}

@media (min-width: 760px) {
  .people-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1600px) {
  .people-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .people-intro {
    padding: 1.15rem 1rem;
  }
}
</style>
